/*
 * Copyright (c) 2023 CalAmp Corp.  All Rights Reserved
 */
import {ServiceTimings} from 'apm/types/types';
import PerformanceMeasurement from './PerformanceMeasurement';

/**
 * @internal
 *
 * Provides a collection for managing all `PerformanceMeasurement` instances in
 * the context of a given service endpoint; allowing for aggregating the total
 * duration as well as the starting and stopping of all measurements within the
 * collection.
 *
 * `ServiceMeasurements` are intended for measuring multiple requests for the
 * same service URL, which can be useful for identifying redundant service
 * requests.
 *
 * This API is primarily intended to be a package level instance, that is, used
 * internally by the underlying PerformanceMonitor instances.
 */
class ServiceMeasurements {
  /**
   * Defines a reference to the service URL for which the `ServiceMeasurements`
   * instance is to monitor.
   */
  protected _url: string;

  /**
   * Provides a reference to the service `PerformanceMeasurement` instances being
   * monitored by the `ServiceMeasurements`.
   */
  protected _measurements: PerformanceMeasurement[];

  /**
   * Provides a reference to the start time of the `ServiceMeasurements` instance,
   * i.e. the time at which the first measurement was started.
   */
  protected _startTime: number;

  /**
   * Provides a reference to the end time of the `ServiceMeasurements` instance,
   * i.e. the time at which the last measurement was completed.
   */
  protected _endTime: number;

  /**
   * The `ServiceMeasurements` constructor initializes the `PerformanceMeasurement`
   * collection.
   *
   * @param url The service URL for which the `ServiceMeasurements` instance is to
   *        monitor.
   */
  constructor(url: string) {
    this._measurements = [];
    this._url = url;
  }

  /**
   * Provides a reference to the `url` for which the `ServiceMeasurements` instance,
   * is monitoring.
   *
   * @returns The url The service URL for which the `ServiceMeasurements` instance
   *          is monitoring.
   */
  get url(): string {
    return this._url;
  }

  /**
   * Provides a getter for accessing the start time of the `PerformanceMeasurement`,
   * i.e. the time at which the measurement was started.
   *
   * @returns The time at which the measurement started, otherwise, `undefined`.
   */
  get startTime(): number | undefined {
    return this._startTime;
  }

  /**
   * Provides a getter for accessing the end time of the `PerformanceMeasurement`,
   * i.e. the time at which the measurement was completed.
   *
   * @returns The time at which the measurement started, otherwise, `undefined`.
   */
  get endTime(): number | undefined {
    return this._endTime;
  }

  /**
   * Provides a getter for retrieving the total duration of all service instances.
   * By default, the duration is calculated based on the start time of the first
   * service, and the stop time of the last service. This ensure the total duration
   * takes into account parallel executions of async service requests and response.
   *
   * To retrieve the total cumulative duration, the `cumulativeDuration` getter can
   * be used.
   *
   * @returns the total duration of all service instances that have been measured.
   */
  get duration(): number {
    return this._endTime - this._startTime;
  }

  /**
   * Provides a getter for retrieving the total cumulative duration of all service
   * instances. The returned value is the sum of all service durations. To retrieve
   * the default duration which is calculated based on the start time of the first
   * service request and the end time of the last service response, use the `duration`
   * getter.
   *
   * @returns the total cumulative duration of all service instances that have been
   *          measured.
   */
  get cumulativeDuration(): number {
    return this._measurements.reduce(
      (acc, {duration}) => acc + duration, 0) ?? 0;
  };

  /**
   * Provides a getter for retrieving the number `PerformanceMeasurement` instances
   * monitored for the given service.
   *
   * @returns the total number of measurements taken for the given service.
   */
  get occurrences(): number {
    return this._measurements?.length;
  }

  /**
   * Adds an additional `PerformanceMeasurement` instance to the given service
   * being measured, and automatically starts measuring the new service instance.
   */
  start() {
    const measurement = new PerformanceMeasurement(true);
    if (!this._startTime) {
      this._startTime = measurement.startTime || performance.now();
    }
    this._measurements.push(measurement);
  }

  /**
   * Immediately stops measuring the first currently running instance and returns
   * the total duration of the instance (if there are any running instances).
   *
   * When the `stopAll` argument is set to `true` (defaults to `false`), each
   * service instance being monitored will immediately be stopped and the total
   * duration of all service instances will be returned.
   *
   * @param   stopAll When `true`, will immediately stop all running service
   *          measurements and return the total duration of all service instances.
   * @returns The duration of the first instance that is currently running, otherwise
   *          when `stopAll` is set to `true`, returns the total duration of all
   *          service instances.
   */
  stop(stopAll = false): number {
    if (stopAll || this.occurrences === 1) {
      this._measurements.forEach(item => item.stop());
      const last = this._measurements.at(this._measurements.length -1);
      this._endTime = last?.endTime || performance.now();
      return this.duration;
    }
    const measurement = this._measurements.find(
      measurement => measurement.isRunning()
    );
    measurement?.stop();
    return measurement?.duration ?? 0;
  }

  /**
   * Determines if there are currently any measurements running for the monitored
   * service.
   *
   * @returns `true` if there are currently any measurements running, otherwise,
   *          `false`.
   */
  hasRunning(): boolean {
    return !!this._measurements.find(
      measurement => measurement.isRunning()
    );
  }

  /**
   * Returns the `ServiceMeasurements` results as an object with a single key
   * based on the `url`, the value for which is an array of all measured items
   * which include the duration by default. To have the each item's start time,
   * end time, and total duration returned, the `durationOnly` argument can be
   * sent to `false`.
   *
   * The `fixedTime` argument can be used to have the result values formatted
   * in milliseconds rather than
   *
   * @param   fixedTime when `true` (default), returns the measurement values
   *          in fixed millisecond format, otherwise, when `false` returns the
   *          measurement values in their raw high precision format.
   * @param   durationOnly when `true` (default), only the `duration` property
   *          will be returned. Otherwise, when `false` the `start` and `end`
   *          times will be returned as well.
   * @returns an object with a single key based on the `url`, the value for which
   *          is an array of all measured items which include each item's start
   *          time, end time, and total duration.
   */
  getResults(fixedTime = true, durationOnly = true): ServiceTimings {
    return {
      [this._url] : this._measurements.map((item: PerformanceMeasurement) => (
        item.getResults(fixedTime, durationOnly)
      ))
    } as unknown as ServiceTimings;
  }
}

export default ServiceMeasurements;
